import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useWalletClient } from 'wagmi';
import { OFT_ABI } from '../config/oft';
import { STARGATE_USDC_POOLS, INK_LZ_EID } from '../config/stargate';

export interface StargateSendParams {
  amountLD: bigint;
  minAmountLD: bigint;
  nativeFee: bigint;
  lzTokenFee: bigint;
}

/**
 * Executes a StargateV2 `send()` to bridge USDC from source chain to Ink.
 * Same interface as OFT send — both built on LayerZero v2.
 *
 * @param sourceChainId - EVM chain ID of the source chain
 */
export function useStargateSend(sourceChainId: number | undefined) {
  const { data: walletClient } = useWalletClient({
    chainId: sourceChainId,
  });
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (params: StargateSendParams) => {
      if (!sourceChainId) {
        throw new Error('sourceChainId is required');
      }
      if (!walletClient) {
        throw new Error('Wallet not connected');
      }

      const poolConfig = STARGATE_USDC_POOLS[sourceChainId];
      if (!poolConfig) {
        throw new Error(`No Stargate USDC pool for chainId ${sourceChainId}`);
      }

      const userAddress = walletClient.account.address;

      // Convert address to bytes32 (left-pad with zeros)
      const addressAsBytes32 = ('0x' +
        '0000000000000000000000000000000000000000000000'.slice(0, 24) +
        userAddress.slice(2).toLowerCase()) as `0x${string}`;

      const sendParam = {
        dstEid: INK_LZ_EID,
        to: addressAsBytes32,
        amountLD: params.amountLD,
        minAmountLD: params.minAmountLD,
        extraOptions: '0x' as `0x${string}`,
        composeMsg: '0x' as `0x${string}`,
        oftCmd: '0x' as `0x${string}`, // Taxi mode (empty)
      };

      const fee = {
        nativeFee: params.nativeFee,
        lzTokenFee: params.lzTokenFee,
      };

      const txHash = await walletClient.writeContract({
        address: poolConfig.poolAddress,
        abi: OFT_ABI,
        functionName: 'send',
        args: [sendParam, fee, userAddress],
        value: params.nativeFee,
        chain: walletClient.chain,
        account: walletClient.account,
      });

      return txHash;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['sourceTokenBalance', sourceChainId],
      });
    },
  });
}
